import { Card, Divider, Space } from "antd";
import React, { useMemo } from "react";
import axios from "../../../utils/axiosInceptor";
import { propertyService } from "../../../configs/constants";
import { QrCodePanel } from "./components/QrCodePanel";
import { SortableImageGallery } from "./components/SortableImageGallery";
import { GallerySourceImage, SaveGalleryPayload } from "./types";

export type ImagesAndQrCodeProps = {
  assetId: string;
  /** Request body key for the id. Use `"unitId"` for units; defaults to `"asset_id"`. */
  idField?: "asset_id" | "unitId";
  galleryImages?: GallerySourceImage[];
  images?: GallerySourceImage[];
  qrValue?: string;
  maxImages?: number;
  showImages?: boolean;
  showQr?: boolean;
  imagesTitle?: string;
  qrTitle?: string;
  qrDescription?: string;
  contentPurpose?: string;
  saveButtonLabel?: string;
  qrFileNamePrefix?: string;
  /** Optional path segments for media upload: org/property[/unit]. */
  uploadPathSegments?: {
    propertyName?: string;
    unitCode?: string;
  };
  onSaveGallery?: (payload: SaveGalleryPayload) => Promise<void>;
  onSaveSuccess?: () => void;
};

const defaultSaveGallery = async (
  payload: SaveGalleryPayload,
  idField: ImagesAndQrCodeProps["idField"] = "asset_id",
) => {
  const entity = idField === "unitId" ? "unit" : "property";
  await axios.patch(`${propertyService}/${entity}/${payload.assetId}/update-fields`, {
    galleryImages: payload.galleryImages,
  });
};

export const ImagesAndQrCode = (props: ImagesAndQrCodeProps) => {
  
  const {
    assetId,
    idField = "asset_id",
    galleryImages,
    images,
    qrValue = "",
    maxImages = 6,
    showImages = true,
    showQr = true,
    imagesTitle = "Images",
    qrTitle = "QR Code",
    qrDescription = "Download the QR code to share or print.",
    contentPurpose = "property_photo",
    saveButtonLabel = "Save Images",
    qrFileNamePrefix = "qr-code",
    uploadPathSegments,
    onSaveGallery,
    onSaveSuccess,
  } = props;

  const sourceImages = useMemo(
    () =>
      (galleryImages ?? images ?? []).map((image) => ({
        ...image,
        documentId: image.documentId || image.data,
        documentUrl: image.documentUrl || image.url,
        url: image.documentUrl || image.url || "",
      })),
    [galleryImages, images],
  );

  const handleSave = onSaveGallery || ((payload: SaveGalleryPayload) => defaultSaveGallery(payload, idField));

  return (
    <Space direction="vertical" size={24} className="w-100">
      {showImages && (
        <Card bordered={false} bodyStyle={{ padding: "16px 24px" }}>
          <SortableImageGallery
            assetId={assetId}
            images={sourceImages}
            maxImages={maxImages}
            title={imagesTitle}
            contentPurpose={contentPurpose}
            saveButtonLabel={saveButtonLabel}
            uploadPathSegments={uploadPathSegments}
            onSave={handleSave}
            onSaveSuccess={onSaveSuccess}
          />
        </Card>
      )}

      {showImages && showQr && <Divider className="m-0" />}

      {showQr && (
        <Card bordered={false} bodyStyle={{ padding: "16px 24px" }}>
          <QrCodePanel
            value={qrValue}
            title={qrTitle}
            description={qrDescription}
            fileNamePrefix={qrFileNamePrefix}
          />
        </Card>
      )}
    </Space>
  );
};

export default ImagesAndQrCode;
